import { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { withRedemptionToken } from '@utils/redemptionRoute';
import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import { LoginValues } from '../context/AuthContext';

type Props = {
  onSubmit(values: LoginValues): Promise<void>;
  disabled?: boolean;
};

export const LoginForm = ({ onSubmit, disabled = false }: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const formSchema = useMemo(
    () =>
      z.object({
        email: z.string().email({ message: t('auth_page.email_error') }),
        password: z.string().min(6, { message: t('auth_page.password_error') }),
      }),
    [t]
  );
  const { register, handleSubmit, formState } = useForm<LoginValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });
  const emailError = formState.touchedFields.email ? formState.errors.email?.message : undefined;
  const passwordError = formState.touchedFields.password
    ? formState.errors.password?.message
    : undefined;
  const isPending = disabled || formState.isSubmitting;

  return (
    <form className="redemption-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div>
        <label htmlFor="login-email">{t('auth_page.email_label')}</label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          required
          disabled={isPending}
          aria-invalid={!!emailError}
          aria-describedby={emailError ? 'login-email-error' : undefined}
          {...register('email')}
        />
        {emailError && (
          <p id="login-email-error" className="redemption-field-error" role="alert">
            {emailError}
          </p>
        )}
      </div>
      <div>
        <div className="redemption-label-row">
          <label htmlFor="login-password">{t('auth_page.password_label')}</label>
          <Link
            to={withRedemptionToken('/forgot-password', token)}
            className="redemption-text-link"
          >
            {t('auth_page.forgot_password')}
          </Link>
        </div>
        <input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          disabled={isPending}
          aria-invalid={!!passwordError}
          aria-describedby={passwordError ? 'login-password-error' : undefined}
          {...register('password')}
        />
        {passwordError && (
          <p id="login-password-error" className="redemption-field-error" role="alert">
            {passwordError}
          </p>
        )}
      </div>
      <button
        type="submit"
        className="redemption-primary"
        disabled={!formState.isValid || isPending}
        aria-busy={isPending}
      >
        {t('auth_page.login_button')}
        <ArrowRight size={18} aria-hidden="true" />
      </button>
    </form>
  );
};
